import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher, fetchInfinite } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';

export type StaffInviteEmail = {
  status: 'created' | 'pending' | 'failed' | 'succeeded' | null;
  postmarkMessageEvent:
    | 'SubscriptionChanged'
    | 'Delivered'
    | 'Transient'
    | 'Opened'
    | 'LinkClicked'
    | 'Bounced'
    | null;
  delivered: boolean;
  deliveredAt: string | null;
  opened: boolean;
  openedAt: string | null;
  bounced: boolean;
  bouncedAt: string | null;
  failedAt: string | null;
  createdAt: string | null;
};

export type StaffInvite = {
  userId: string;
  email: string;
  name?: string;
  status: 'pending' | 'active' | 'revoked' | 'deleted';
  role?: 'scanner';
  /** Ticket-type ObjectIds the staffer is restricted to; empty = all. */
  allowedTicketTypes?: string[];
  invitedAt?: string;
  acceptedAt?: string;
  lastSeenAt?: string;
  inviteEmail?: StaffInviteEmail;
};

export type InviteEntry = {
  email: string;
  name?: string;
  /** Per-entry ticket-type whitelist; empty/missing = all. */
  allowedTicketTypes?: string[];
};

export type InviteOutcome = 'created' | 'reused' | 're-invited' | 'error';

export type InviteResult = {
  email: string;
  outcome: InviteOutcome;
  userId?: string;
};

export type StaffSortField = 'invitedAt' | 'lastSeenAt' | 'status';
export type StaffSortDir = 'asc' | 'desc';
/** Server-side membership status filter. Mirrors the API enum. */
export type StaffStatusFilter = 'pending' | 'active' | 'revoked';

export type StaffListOpts = {
  includeDeleted?: boolean;
  page?: number;
  limit?: number;
  q?: string;
  sort?: StaffSortField;
  dir?: StaffSortDir;
  status?: StaffStatusFilter;
};

const key = (
  pageId: string,
  opts: Omit<Required<StaffListOpts>, 'q' | 'status'> & { q: string; status: string },
) => ['staff-invites', pageId, opts];

export function useStaffInvites(pageId: string, sellerId: string, opts: StaffListOpts = {}) {
  const includeDeleted = !!opts.includeDeleted;
  const pageNum = opts.page ?? 1;
  const limit = opts.limit ?? 20;
  const q = opts.q ?? '';
  const sort: StaffSortField = opts.sort ?? 'invitedAt';
  const dir: StaffSortDir = opts.dir ?? 'desc';
  const status = opts.status;

  const { token } = useAuth();
  const qc = useQueryClient();

  const queryString = (() => {
    const params = new URLSearchParams();
    if (includeDeleted) params.set('includeDeleted', 'true');
    if (q.trim()) params.set('q', q.trim());
    if (status) params.set('status', status);
    params.set('page', String(pageNum));
    params.set('limit', String(limit));
    params.set('sort', sort);
    params.set('dir', dir);
    return params.toString();
  })();

  const queryResult = useQuery<{ data: StaffInvite[]; total: number; has_more: boolean }>({
    queryKey: key(pageId, {
      includeDeleted,
      page: pageNum,
      limit,
      q,
      sort,
      dir,
      status: status ?? '',
    }),
    queryFn: () =>
      fetchInfinite(`/scanner/${pageId}/staff-invites?${queryString}`, token).then(
        (res: { data?: StaffInvite[]; total?: number; has_more?: boolean } | undefined) => ({
          data: (res?.data ?? []) as StaffInvite[],
          total: res?.total ?? 0,
          has_more: !!res?.has_more,
        }),
      ),
    enabled: !!pageId && !!token,
    placeholderData: previous => previous,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['staff-invites', pageId] });
    // Past-staff pool also reads memberships — keep it in sync.
    qc.invalidateQueries({ queryKey: ['staff-pool'] });
  };

  const toastError = (title: string) => (error: unknown) =>
    toast({
      title,
      description: (error as { message?: string } | null)?.message || 'Try again in a moment.',
      type: 'alert',
    });

  const inviteMutation = useMutation({
    mutationFn: (invites: InviteEntry[]) =>
      fetcher(`/scanner/${pageId}/staff-invites`, token, {
        method: 'POST',
        body: JSON.stringify({ invites }),
      }).then((data: InviteResult[] | undefined) => data ?? []),
    onSuccess: invalidate,
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) =>
      fetcher(`/scanner/${pageId}/staff-invites/${userId}/revoke`, token, { method: 'POST' }),
    onSuccess: invalidate,
  });

  // "Delete" — removes the staffer's membership across every event under
  // this seller. Hits the new POST /scanner/staff/:userId/remove-from-seller
  // endpoint rather than the per-event DELETE /staff-invites/:userId.
  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      fetcher(
        `/scanner/staff/${userId}/remove-from-seller?sellerId=${encodeURIComponent(sellerId)}`,
        token,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: ({
      userId,
      allowedTicketTypes,
    }: {
      userId: string;
      allowedTicketTypes: string[];
    }) =>
      fetcher(`/scanner/${pageId}/staff-invites/${userId}/permissions`, token, {
        method: 'PATCH',
        body: JSON.stringify({ allowedTicketTypes }),
      }),
    onSuccess: invalidate,
  });

  const resendMutation = useMutation({
    mutationFn: (userId: string) =>
      fetcher(`/scanner/${pageId}/staff-invites/${userId}/resend`, token, { method: 'POST' }),
    onSuccess: invalidate,
  });

  type SilentOnly = { silent?: boolean } | undefined;
  const resendWithToast = (userId: string, opts?: SilentOnly): Promise<unknown> =>
    resendMutation.mutateAsync(userId, {
      onError: opts?.silent ? undefined : toastError("Couldn't resend invite"),
      onSuccess: opts?.silent ? undefined : () => toast({ title: 'Invited 1 staff member' }),
    });

  type SilentOpt = { silent?: boolean } | undefined;
  const withToast =
    <V, R>(
      mutation: ReturnType<typeof useMutation<R, any, V>>,
      failureTitle: string,
      successTitle?: string,
    ) =>
    (vars: V, opts?: SilentOpt): Promise<R> =>
      mutation.mutateAsync(
        vars,
        opts?.silent
          ? {}
          : {
              onError: toastError(failureTitle),
              ...(successTitle ? { onSuccess: () => toast({ title: successTitle }) } : {}),
            },
      );

  return {
    invites: queryResult.data?.data ?? [],
    total: queryResult.data?.total ?? 0,
    hasMore: queryResult.data?.has_more ?? false,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    invite: withToast(inviteMutation, "Couldn't send the invite"),
    revoke: withToast(
      revokeMutation,
      "Couldn't remove access",
      'Removed access for 1 staff member',
    ),
    remove: withToast(removeMutation, "Couldn't delete staffer"),
    resend: resendWithToast,
    updatePermissions: withToast(updatePermissionsMutation, "Couldn't update permissions"),
    // Pending state per userId
    pending: {
      invite: inviteMutation.isPending,
      inviteVariables: inviteMutation.variables as InviteEntry[] | undefined,
      revoke: revokeMutation.isPending ? (revokeMutation.variables as string) : null,
      remove: removeMutation.isPending ? (removeMutation.variables as string) : null,
      resend: resendMutation.isPending ? (resendMutation.variables as string) : null,
    },
  };
}
